import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import StoryCard from '../components/StoryCard';
import WowMeter from '../components/WowMeter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, Mail, Sparkles, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

export default function HomePage() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [heroStories, setHeroStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const fetchStories = useCallback(async (pageNum = 0, append = false) => {
    try {
      const res = await axios.get(`${API}/stories?skip=${pageNum * 20}&limit=20`);
      if (res.data.length < 20) setHasMore(false);
      
      if (append) {
        setStories(prev => [...prev, ...res.data]);
      } else {
        setStories(res.data);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  }, []);

  const fetchHeroStories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stories/hero/featured`);
      setHeroStories(res.data);
    } catch (error) {
      console.error('Error fetching hero stories:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStories(), fetchHeroStories(), fetchCategories()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStories, fetchHeroStories, fetchCategories]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchStories(nextPage, true);
  };

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/signup`, { email });
      toast.success('Subscribed to daily digest!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden hex-pattern">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1761232007928-258c9bd98d66?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] text-cyan-400/80 mb-6 animate-fade-in-up" data-testid="hero-subtitle">
            THE COMPLETE ARCHIVE
          </p>
          
          <h1 className="masthead text-4xl sm:text-6xl md:text-8xl mb-6 animate-fade-in-up stagger-1" data-testid="hero-title">
            RES TOTA IPSUM
          </h1>
          
          <p className="font-serif text-xl md:text-2xl text-white/70 mb-4 italic animate-fade-in-up stagger-2">
            The Whole of It Laid Out Before You
          </p>
          
          <p className="text-white/50 max-w-2xl mx-auto mb-10 animate-fade-in-up stagger-3">
            Everything — government programs, high-strangeness, consciousness research, and personal awe — 
            is one intelligent geometric web hiding in plain sight. We reveal the complete picture.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up stagger-4">
            <Link to="/explorer">
              <Button className="btn-primary flex items-center gap-2" data-testid="explore-btn">
                <Sparkles className="w-4 h-4" />
                Explore Connections
              </Button>
            </Link>
            <Link to="/analyzer">
              <Button className="btn-outline flex items-center gap-2" data-testid="analyzer-btn">
                Ask AI Analyzer
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ArrowDown className="w-6 h-6 text-white/30" />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/?category=${cat.id}`}
              className="card-dark p-4 text-center group"
              data-testid={`category-${cat.id}`}
            >
              <h3 className="font-mono text-xs uppercase tracking-wider text-white/70 group-hover:text-cyan-400 transition-colors mb-1">
                {cat.name}
              </h3>
              <p className="text-white/30 text-xs line-clamp-2">{cat.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Hero Stories */}
      {heroStories.length > 0 && (
        <section className="py-8 px-4 md:px-8 max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-cyan-400" />
            Featured
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {heroStories.slice(0, 1).map(story => (
              <StoryCard key={story.story_id} story={story} size="hero" />
            ))}
            <div className="col-span-full md:col-span-4 space-y-4">
              {heroStories.slice(1, 4).map(story => (
                <StoryCard key={story.story_id} story={story} size="compact" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Feed - Tetris Grid */}
      <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto" data-testid="stories-section">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Feed */}
          <div className="flex-1">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-white/20" />
              The Whole Picture
            </h2>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-8 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={`skeleton h-64 rounded-sm ${i === 0 ? 'col-span-full md:col-span-8' : 'col-span-full md:col-span-4'}`} />
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-8 gap-6">
                  {stories.map((story, idx) => (
                    <StoryCard
                      key={story.story_id}
                      story={story}
                      size={idx % 5 === 0 ? 'hero' : 'normal'}
                    />
                  ))}
                </div>

                {hasMore && (
                  <div className="text-center mt-12">
                    <Button onClick={loadMore} className="btn-outline" data-testid="load-more-btn">
                      Load More Stories
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-6">
            {/* Wow Meter */}
            {user && <WowMeter compact />}
            
            {/* Quick Links */}
            <div className="card-dark p-4">
              <h3 className="font-mono text-xs uppercase tracking-wider text-white/40 mb-4">Explore</h3>
              <div className="space-y-2">
                <Link to="/explorer" className="block text-white/70 hover:text-cyan-400 transition-colors text-sm">
                  Phenomena Explorer
                </Link>
                <Link to="/people" className="block text-white/70 hover:text-cyan-400 transition-colors text-sm">
                  People Database
                </Link>
                <Link to="/analyzer" className="block text-white/70 hover:text-cyan-400 transition-colors text-sm">
                  AI Analyzer
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 px-4 md:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="w-12 h-12 mx-auto mb-6 text-cyan-400" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Daily Digest
          </h2>
          <p className="text-white/50 mb-8">
            Get the most compelling connections and revelations delivered to your inbox every morning.
          </p>
          
          <form onSubmit={handleNewsletterSignup} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="newsletter-email"
            />
            <Button type="submit" className="btn-primary" disabled={subscribing} data-testid="newsletter-submit">
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="masthead text-lg mb-2">RES TOTA IPSUM</p>
              <p className="text-white/40 text-sm">
                Fact-based archive from public sources. Zero sensationalism.
              </p>
            </div>
            
            <div className="flex items-center gap-6 text-white/40 text-sm">
              <Link to="/people" className="hover:text-white transition-colors">People</Link>
              <Link to="/explorer" className="hover:text-white transition-colors">Explorer</Link>
              <Link to="/media" className="hover:text-white transition-colors">Library</Link>
              <Link to="/community" className="hover:text-white transition-colors">Community</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/5 text-center text-white/30 text-xs">
            <p>Disclaimer: All content is sourced from public documents, interviews, and published research.</p>
            <p className="mt-2">© 2026 Res Tota Ipsum. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
